import React, { useState, useEffect, Fragment } from 'react';
import { Newspaper, ExternalLink, Calendar, RefreshCw, X, ChevronDown } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';
import AdSense from './AdSense';
import NewsSummaryModal from './NewsSummaryModal';
import ArticleContentModal from './ArticleContentModal';
import CompanyLogo from './CompanyLogo';
import { db } from '../firebase/config';
import { collection, query, orderBy, limit, getDocs, where, doc, getDoc, startAfter } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

export default function NewsFeedPage({ user, userRole }) {
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('market'); // 'market', 'watchlist', 'theme', 'all', 'allThemes'
  const [availableThemes, setAvailableThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [allThemes, setAllThemes] = useState([]);
  const [showAllThemesDropdown, setShowAllThemesDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Infinite scroll state variables
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [modalMode, setModalMode] = useState('summary'); // 'summary' or 'article'
  const [iframeError, setIframeError] = useState(false);

  // Load available themes from Firebase when user changes
  useEffect(() => {
    loadAvailableThemes();
  }, [user]);

  // Load all themes on component mount
  useEffect(() => {
    loadAllThemes();
  }, []);

  useEffect(() => {
    loadNews();
  }, [filter, selectedTheme, user]);

  const loadAvailableThemes = async () => {
    try {
      if (!user) {
        setAvailableThemes([]);
        return;
      }

      // Get user's selected themes from their watchlistThemes field
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userThemeKeys = userDoc.data()?.watchlistThemes || [];

      if (userThemeKeys.length === 0) {
        setAvailableThemes([]);
        return;
      }

      // Load full theme data for each user-selected theme
      const themes = await Promise.all(
        userThemeKeys.map(async (themeKey) => {
          const themeDoc = await getDoc(doc(db, 'investmentThemes', themeKey));
          if (themeDoc.exists()) {
            return {
              key: themeDoc.id,
              name: themeDoc.data().name || themeDoc.id,
              tickers: themeDoc.data().tickers || []
            };
          }
          return null;
        })
      );

      // Filter out null values (themes that don't exist)
      const validThemes = themes.filter(theme => theme !== null);

      // Sort themes alphabetically by name
      validThemes.sort((a, b) => a.name.localeCompare(b.name));

      setAvailableThemes(validThemes);

      // Set first theme as default if available
      if (validThemes.length > 0 && !selectedTheme) {
        setSelectedTheme(validThemes[0].key);
      }
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const loadAllThemes = async () => {
    try {
      const themesRef = collection(db, 'investmentThemes');
      const querySnapshot = await getDocs(themesRef);
      const themes = querySnapshot.docs.map(doc => ({
        key: doc.id,
        name: doc.data().name || doc.id,
        tickers: doc.data().tickers || []
      }));

      // Sort themes alphabetically by name
      themes.sort((a, b) => a.name.localeCompare(b.name));

      setAllThemes(themes);
    } catch (error) {
      console.error('Error loading all themes:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const themeDropdown = document.querySelector('.theme-dropdown-container');
      const allThemesDropdown = document.querySelector('.all-themes-dropdown-container');

      // Close My Themes dropdown if clicking outside
      if (themeDropdown && !themeDropdown.contains(event.target) && showThemeDropdown) {
        setShowThemeDropdown(false);
      }

      // Close All Themes dropdown if clicking outside
      if (allThemesDropdown && !allThemesDropdown.contains(event.target) && showAllThemesDropdown) {
        setShowAllThemesDropdown(false);
      }
    };

    if (showThemeDropdown || showAllThemesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThemeDropdown, showAllThemesDropdown]);

  const handleDeleteArticle = async (article) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/news/${article.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setNews(prev => prev.map(n => 
          n.id === article.id ? { ...n, scrapedFullText: null, parsedAt: null } : n
        ));
      } else {
        alert('Failed to clear article content');
      }
    } catch (error) {
      console.error('Error clearing article content:', error);
      alert('Error clearing article content');
    }
  };

  const loadNews = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLastVisible(null);
        setHasMore(true);
      }
      setError(null);

      const ITEMS_PER_PAGE = 20; // Load 20 items at a time

      if (filter === 'market') {
        // Market news - show most popular/important news
        const newsRef = collection(db, 'news');
        let q;

        if (loadMore && lastVisible) {
          q = query(
            newsRef,
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(ITEMS_PER_PAGE)
          );
        } else {
          q = query(
            newsRef,
            orderBy('createdAt', 'desc'),
            limit(ITEMS_PER_PAGE)
          );
        }

        const querySnapshot = await getDocs(q);

        if (querySnapshot.docs.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }

        if (querySnapshot.docs.length > 0) {
          setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        }

        const newsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            publishedAt: typeof data.publishedAt === 'string'
              ? new Date(data.publishedAt)
              : data.publishedAt?.toDate?.() || new Date()
          };
        });

        newsData.sort((a, b) => b.publishedAt - a.publishedAt);

        if (loadMore) {
          setNews(prevNews => [...prevNews, ...newsData]);
        } else {
          setNews(newsData);
        }

      } else if (filter === 'watchlist' && user) {
        // Load watchlist tickers first
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const watchlist = userDoc.data()?.watchlist || [];

        if (watchlist.length === 0) {
          setNews([]);
          setHasMore(false);
          setLoading(false);
          setLoadingMore(false);
          return;
        }

        // For watchlist, load all news and handle pagination in memory
        if (!loadMore) {
          const newsPromises = watchlist.map(async (ticker) => {
            const newsRef = collection(db, 'news');
            const q = query(
              newsRef,
              where('ticker', '==', ticker),
              orderBy('createdAt', 'desc'),
              limit(100)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                publishedAt: typeof data.publishedAt === 'string'
                  ? new Date(data.publishedAt)
                  : data.publishedAt?.toDate?.() || new Date()
              };
            });
          });

          const newsArrays = await Promise.all(newsPromises);
          const allNews = newsArrays.flat();
          allNews.sort((a, b) => b.publishedAt - a.publishedAt);

          setNews(allNews.slice(0, ITEMS_PER_PAGE));
          setHasMore(allNews.length > ITEMS_PER_PAGE);

          // Store full list for pagination
          window.fullNewsList = allNews;
        } else {
          // Load more from cached list
          const currentLength = news.length;
          const nextBatch = (window.fullNewsList || []).slice(currentLength, currentLength + ITEMS_PER_PAGE);

          if (nextBatch.length < ITEMS_PER_PAGE) {
            setHasMore(false);
          }

          setNews(prevNews => [...prevNews, ...nextBatch]);
        }

      } else if (filter === 'theme') {
        const themeDoc = await getDoc(doc(db, 'investmentThemes', selectedTheme));

        let themeTickers = [];
        if (themeDoc.exists()) {
          const themeData = themeDoc.data();
          if (themeData.tickers && Array.isArray(themeData.tickers)) {
            themeTickers = themeData.tickers;
          } else if (themeData.stocks && Array.isArray(themeData.stocks)) {
            themeTickers = themeData.stocks.map(stock => stock.ticker);
          }
        }

        if (themeTickers.length === 0) {
          setNews([]);
          setHasMore(false);
          setLoading(false);
          setLoadingMore(false);
          return;
        }

        // Load all theme news and paginate in memory
        if (!loadMore) {
          const newsPromises = themeTickers.slice(0, 20).map(async (ticker) => {
            const newsRef = collection(db, 'news');
            const q = query(
              newsRef,
              where('ticker', '==', ticker),
              orderBy('createdAt', 'desc'),
              limit(50)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                publishedAt: typeof data.publishedAt === 'string'
                  ? new Date(data.publishedAt)
                  : data.publishedAt?.toDate?.() || new Date()
              };
            });
          });

          const newsArrays = await Promise.all(newsPromises);
          const allNews = newsArrays.flat();
          allNews.sort((a, b) => b.publishedAt - a.publishedAt);

          setNews(allNews.slice(0, ITEMS_PER_PAGE));
          setHasMore(allNews.length > ITEMS_PER_PAGE);
          window.fullNewsList = allNews;
        } else {
          const currentLength = news.length;
          const nextBatch = (window.fullNewsList || []).slice(currentLength, currentLength + ITEMS_PER_PAGE);

          if (nextBatch.length < ITEMS_PER_PAGE) {
            setHasMore(false);
          }

          setNews(prevNews => [...prevNews, ...nextBatch]);
        }

      } else if (filter === 'allThemes') {
        if (!selectedTheme) {
          setNews([]);
          setHasMore(false);
          setLoading(false);
          setLoadingMore(false);
          return;
        }

        const themeDoc = await getDoc(doc(db, 'investmentThemes', selectedTheme));

        let themeTickers = [];
        if (themeDoc.exists()) {
          const themeData = themeDoc.data();
          if (themeData.tickers && Array.isArray(themeData.tickers)) {
            themeTickers = themeData.tickers;
          } else if (themeData.stocks && Array.isArray(themeData.stocks)) {
            themeTickers = themeData.stocks.map(stock => stock.ticker);
          }
        }

        if (themeTickers.length === 0) {
          setNews([]);
          setHasMore(false);
          setLoading(false);
          setLoadingMore(false);
          return;
        }

        if (!loadMore) {
          const newsPromises = themeTickers.slice(0, 20).map(async (ticker) => {
            const newsRef = collection(db, 'news');
            const q = query(
              newsRef,
              where('ticker', '==', ticker),
              orderBy('createdAt', 'desc'),
              limit(50)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                publishedAt: typeof data.publishedAt === 'string'
                  ? new Date(data.publishedAt)
                  : data.publishedAt?.toDate?.() || new Date()
              };
            });
          });

          const newsArrays = await Promise.all(newsPromises);
          const allNews = newsArrays.flat();
          allNews.sort((a, b) => b.publishedAt - a.publishedAt);

          setNews(allNews.slice(0, ITEMS_PER_PAGE));
          setHasMore(allNews.length > ITEMS_PER_PAGE);
          window.fullNewsList = allNews;
        } else {
          const currentLength = news.length;
          const nextBatch = (window.fullNewsList || []).slice(currentLength, currentLength + ITEMS_PER_PAGE);

          if (nextBatch.length < ITEMS_PER_PAGE) {
            setHasMore(false);
          }

          setNews(prevNews => [...prevNews, ...nextBatch]);
        }

      } else if (filter === 'all') {
        // All news - use Firestore pagination
        const newsRef = collection(db, 'news');
        let q;

        if (loadMore && lastVisible) {
          q = query(
            newsRef,
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(ITEMS_PER_PAGE)
          );
        } else {
          q = query(
            newsRef,
            orderBy('createdAt', 'desc'),
            limit(ITEMS_PER_PAGE)
          );
        }

        const querySnapshot = await getDocs(q);

        if (querySnapshot.docs.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }

        if (querySnapshot.docs.length > 0) {
          setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        }

        const newsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            publishedAt: typeof data.publishedAt === 'string'
              ? new Date(data.publishedAt)
              : data.publishedAt?.toDate?.() || new Date()
          };
        });

        newsData.sort((a, b) => b.publishedAt - a.publishedAt);

        if (loadMore) {
          setNews(prevNews => [...prevNews, ...newsData]);
        } else {
          setNews(newsData);
        }
      }
    } catch (error) {
      console.error('Error loading news:', error);
      if (error.message && error.message.includes('index')) {
        setError('Firebase index is being created. Please wait 1-2 minutes and refresh the page.');
      } else if (error.message && error.message.includes('permission')) {
        setError('Permission denied. Please check Firebase security rules.');
      } else {
        setError('Failed to load news');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  // Infinite scroll detection
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.documentElement.offsetHeight - 500; // Trigger 500px before bottom

      if (scrollPosition >= bottomPosition) {
        loadNews(true); // Load more items
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, news.length, filter, selectedTheme]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);

    // For now, just reload the news from database
    // News collection runs automatically every 2 hours
    await loadNews();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    // Show full date and time
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading && news.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading news...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Newspaper className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">News</h1>
        </div>
        {/* Refresh Button - Admin Only */}
        {userRole === USER_ROLES.ADMIN && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            title="Reload news from database (auto-collection runs every 2 hours)"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Reload
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-6 pb-2 overflow-visible">
        <button
          onClick={() => {
            setFilter('market');
            setShowThemeDropdown(false);
            setShowAllThemesDropdown(false);
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            filter === 'market'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('news.marketNews')}
        </button>

        <button
          onClick={() => {
            setFilter('watchlist');
            setShowThemeDropdown(false);
            setShowAllThemesDropdown(false);
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            filter === 'watchlist'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('news.myWatchlistNews')}
        </button>

        {/* My Themes News with Dropdown */}
        <div className="relative theme-dropdown-container">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (filter === 'theme') {
                // Toggle dropdown if already on theme filter
                setShowThemeDropdown(!showThemeDropdown);
              } else {
                // Switch to theme filter and open dropdown
                setFilter('theme');
                setShowThemeDropdown(true);
              }
              setShowAllThemesDropdown(false);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              filter === 'theme'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('news.myThemesNews')}
            <ChevronDown className={`w-4 h-4 transition-transform ${showThemeDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* Theme Dropdown - Show when dropdown is open */}
          {showThemeDropdown && (
            <div
              className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[220px] max-h-[300px] overflow-y-auto"
              style={{ zIndex: 9999 }}
              onClick={(e) => e.stopPropagation()}
            >
              {availableThemes.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No themes selected. Add themes from the Themes page.
                </div>
              ) : (
                availableThemes.map((theme) => (
                  <button
                    key={theme.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Selected theme:', theme.key, theme.name);
                      if (selectedTheme === theme.key) {
                        // Force reload if same theme clicked
                        setSelectedTheme(null);
                        setTimeout(() => setSelectedTheme(theme.key), 0);
                      } else {
                        setSelectedTheme(theme.key);
                      }
                      setShowThemeDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer ${
                      selectedTheme === theme.key ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    {theme.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => {
            setFilter('all');
            setShowThemeDropdown(false);
            setShowAllThemesDropdown(false);
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            filter === 'all'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('news.allNews')}
        </button>

        {/* Themes News with Dropdown (All Themes) */}
        <div className="relative all-themes-dropdown-container">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (filter === 'allThemes') {
                // Toggle dropdown if already on allThemes filter
                setShowAllThemesDropdown(!showAllThemesDropdown);
              } else {
                // Switch to allThemes filter and open dropdown
                setFilter('allThemes');
                setShowAllThemesDropdown(true);
              }
              setShowThemeDropdown(false);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              filter === 'allThemes'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('news.allThemesNews')}
            <ChevronDown className={`w-4 h-4 transition-transform ${showAllThemesDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* All Themes Dropdown */}
          {showAllThemesDropdown && (
            <div
              className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[220px] max-h-[300px] overflow-y-auto"
              style={{ zIndex: 9999 }}
              onClick={(e) => e.stopPropagation()}
            >
              {allThemes.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No themes available
                </div>
              ) : (
                allThemes.map((theme) => (
                  <button
                    key={theme.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Selected all theme:', theme.key, theme.name);
                      if (selectedTheme === theme.key) {
                        // Force reload if same theme clicked
                        setSelectedTheme(null);
                        setTimeout(() => setSelectedTheme(theme.key), 0);
                      } else {
                        setSelectedTheme(theme.key);
                      }
                      setShowAllThemesDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer ${
                      selectedTheme === theme.key ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    {theme.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Current Theme Display */}
      {filter === 'theme' && selectedTheme && (
        <div className="mb-4 text-sm text-gray-600">
          Showing news for: <span className="font-semibold text-gray-900">{availableThemes.find(t => t.key === selectedTheme)?.name || selectedTheme}</span>
        </div>
      )}

      {/* All Themes Display */}
      {filter === 'allThemes' && selectedTheme && (
        <div className="mb-4 text-sm text-gray-600">
          Showing news for: <span className="font-semibold text-gray-900">{allThemes.find(t => t.key === selectedTheme)?.name || selectedTheme}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && news.length === 0 && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <Newspaper className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No news available</h3>
          <p className="text-gray-600">
            {filter === 'watchlist' && 'Add stocks to your watchlist to see related news'}
            {filter === 'theme' && `No news found for ${availableThemes.find(t => t.key === selectedTheme)?.name || selectedTheme} theme`}
            {filter === 'allThemes' && `No news found for ${allThemes.find(t => t.key === selectedTheme)?.name || selectedTheme} theme`}
            {(filter === 'market' || filter === 'all') && 'News will appear here once collected'}
          </p>
        </div>
      )}

      {/* News List - Chronological Order */}
      {news.length > 0 && (
        <div className="space-y-3">
          {news.map((article, index) => {
            const showAdAfter = (index + 1) % 3 === 0; // Show ad every 3 news articles
            return (
              <Fragment key={article.id}>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                {/* Company Logo with Ticker Below */}
                {article.ticker && (
                  <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    <CompanyLogo ticker={article.ticker} size="lg" />
                    <span className="text-xs font-bold text-gray-600">
                      {article.ticker}
                    </span>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {/* Title Only */}
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 line-clamp-2">
                    {article.title}
                  </h3>

                  {/* Footer with Date, Source, and Action Buttons */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.publishedAt)}
                      </span>
                      {article.source && (
                        <span 
                          className="flex items-center gap-1"
                          style={{
                            color: article.isOfficialSource ? '#10b981' : '#9ca3af',
                            fontWeight: article.isOfficialSource ? '600' : '400'
                          }}
                        >
                          {article.isOfficialSource && '🏢'}
                          {article.source}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedArticle(article);
                          setModalMode('summary');
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
                      >
                        <Newspaper className="w-3 h-3" />
                        요약 보기
                      </button>
                      <button
                        onClick={() => {
                          setSelectedArticle(article);
                          setModalMode('article');
                          setIframeError(false);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg font-medium transition-colors"
                      >
                        원문 보기
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {showAdAfter && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <AdSense
                  slot="5107198814"
                />
              </div>
            )}
            </Fragment>
            );
          })}
        </div>
      )}

      {/* Loading More Indicator */}
      {loading && news.length > 0 && (
        <div className="flex justify-center mt-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}


      {/* Article Modal */}
      {selectedArticle && modalMode === 'article' && (
        <ArticleContentModal
          article={selectedArticle}
          onClose={() => {
            setSelectedArticle(null);
            setModalMode('summary');
          }}
          userRole={userRole}
          onDelete={handleDeleteArticle}
        />
      )}

      {/* Summary Modal */}
      {modalMode === 'summary' && (
        <NewsSummaryModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onViewOriginal={() => {
            setModalMode('article');
          }}
          userRole={userRole}
          onDelete={handleDeleteArticle}
        />
      )}
    </div>
  );
}
